import { useCallback, useEffect, useImperativeHandle, useRef, useState, type Ref } from 'react';
import { dimensionLabelKey, PlanView, roomMoveHandlePoint, rotateHandlePoint, yawTowards, type DimensionLabel, type HitTarget, type PlanDraft, type PlanGuide, type Viewport } from '../render/plan/PlanView';
import { DimensionInput } from './DimensionInput';
import { addWall, moveRoom, moveVertex, splitWall, wallDragSegment, contains, projectPoint, samePoint, MIN_WALL } from '../core/geometry/edit';
import { smartSnapItem, snapOpeningOffset, snapVertexPoint, snapRoomTranslation, snapWallTranslation, type PointSnapCapture, type PointSnapResult, type SnapCapture } from '../core/geometry/snapping';
import type { LocalFootprint } from '../core/geometry/footprint';
import type { PlanFurnitureLayer } from '../render/three/PlanTopView';
import type { SurfaceProbe } from '../core/geometry/stacking';
import { ANCHOR_ZONE, TOUCH_ANCHOR_LIFT, constrainMove, exceedsDragThreshold, moveItemGroup, planDragLost, previewItemMove, pullAnchor, standardAngle, wallQuad, type ItemMoveGesture, type MoveInput } from '../state/planGesture';
import { store, type EditorState, type Selection } from '../state/store';
import { projectOpening, rectangularRoom, type OpeningKind } from '../state/planConstruction';
import { contextSelection, movableItems, nextSelectionOnTap, selectedItemIds, toggleItemSelection } from '../state/selectionTap';
import { useStore, shallowEqual } from './useStore';
import type { Item, Level, Vec2, Wall } from '../core/hp/types';
import { previewItemResize, resizeAxisVector, type ResizeBounds, type ResizeFace } from '../state/itemResize';
import { areaCentre, withinArea, REVEAL_INSET, type ScreenPoint, type ScreenRect } from '../state/itemReveal';
import { previewOpeningResize } from '../state/openingProperties';
import { PLAN_SCALE_100, readProjectView, zoomPercent } from '../state/projectView';
import { publishViewZoom } from './viewZoom';
import { floorBelow } from '../state/floors';
import { wellsDown, wellsKey, wellsUp } from '../core/geometry/stairwells';
import { subscribeModelBounds } from '../core/geometry/modelBounds';
import { onSpace, spaceHeld } from '../state/spacePan';
import type { PresenceViewport } from '../collaboration/protocol';
import { applyPlanViewport, capturePlanViewport } from '../collaboration/presence';
import { cursorStyle, planCursor, rotateGlyphAngle } from '../render/three/cursors';
import { chipText, hideGestureChip, itemChip, planChip, showGestureChip } from './gestureChip';
import { releaseResizeHot, resizeHot, setResizeHot, subscribeResizeHot } from './resizeHot';
import { chromeRects } from './chrome';
import type { ContextPressResult } from './contextPress';
import { t as tr } from '../i18n/I18n';

/** Space held, or a project opened only to look at: a press pans and nothing lights up; the ruler still measures. */
const panOnly = (): boolean => spaceHeld() || (store.get().edit === 'none' && store.get().tool !== 'measure');
/** In a shared project only items move: a press on a wall, an opening or the selected room selects it and pans. */
const itemsOnly = (): boolean => store.get().edit === 'items';
const ARCHITECTURE_GRABS: ReadonlySet<NonNullable<HitTarget>['kind']> = new Set(['vertex', 'wall-mid', 'wall', 'opening', 'opening-resize', 'room-move', 'resize']);
const architectureGrab = (target: HitTarget, level: Level): boolean => !!target && (ARCHITECTURE_GRABS.has(target.kind)
  || (target.kind === 'rotate' && level.items.find(item => item.id === target.id)?.placement === 'wall'));

type PointerInput = { clientX: number; clientY: number; altKey: boolean; ctrlKey: boolean; metaKey: boolean; shiftKey: boolean };
/** `floor`: the plan as the press found it, which the first pull checks before it opens the stroke. */
type DragBase = { projectId: string; levelIndex: number; floor: Level; initialSelection: Selection; initialAnchor: { x: number; y: number } | null; footprints: Map<string, LocalFootprint> };
/**
 * `gathered`: a Shift press that already changed the group — its release selects nothing more. `toggles`: a press on what a
 * shared project does not let move — released in place, it keeps what it selected or lets go of what was selected already.
 */
type Drag = DragBase & ({ kind: 'pan'; lastX: number; lastY: number; secondary?: boolean; space?: boolean; gathered?: boolean; toggles?: boolean }
  | { kind: 'move'; gesture: ItemMoveGesture; grab: Vec2; axis?: 'x' | 'z' }
  /** A Shift-gathered group pulled by one of its items, `pressed`; `start` is where the pull began on the plan. */
  | { kind: 'group-move'; ids: string[]; pressed: string; base: Level; start: Vec2; axis?: 'x' | 'z'; held: number }
  | { kind: 'room-move'; index: number; base: Level; axis?: 'x' | 'z'; valid: boolean }
  | { kind: 'rotate'; id: string }
  | { kind: 'resize'; id: string; base: Level; bounds: ResizeBounds; face: ResizeFace }
  | { kind: 'opening-resize'; id: string; base: Level; side: -1 | 1 }
  | { kind: 'opening'; id: string }
  | { kind: 'vertex'; id: string; end: 'start' | 'end'; base: Level; origin: Vec2 }
  /** The corner-to-be at a wall's middle: `split` is the plan with the corner in it, once the pull began. */
  | { kind: 'split'; id: string; base: Level; origin: Vec2; split: Level | null }
  | { kind: 'wall'; id: string; base: Level; segment?: Wall; axis?: 'x' | 'z'; captures?: string[] }
  | { kind: 'insert'; openingKind: OpeningKind; base: Level }
  | { kind: 'room'; start: Vec2; base: Level }
  /** The ruler pulled from `start` (the app's measuring mode). */
  | { kind: 'measure'; start: Vec2; base: Level }
  | { kind: 'draw'; start: Vec2; base: Level; chain: boolean; anchor: Vec2 });

/** The far ends of exactly two walls meeting at `corner`: the line on which that corner makes them one straight wall. */
const straightLine = (meeting: Level['walls'], corner: Vec2): [Vec2, Vec2] | undefined =>
  meeting.length === 2 ? [samePoint(meeting[0].start, corner) ? meeting[0].end : meeting[0].start, samePoint(meeting[1].start, corner) ? meeting[1].end : meeting[1].start] : undefined;

const pointerInput = (event: PointerInput): PointerInput => ({ clientX: event.clientX, clientY: event.clientY,
  altKey: event.altKey, ctrlKey: event.ctrlKey, metaKey: event.metaKey, shiftKey: event.shiftKey });

/** Safari's trackpad pinch: `scale` is how far the fingers have spread since `gesturestart`. */
type GestureLikeEvent = Event & { scale?: number; clientX: number; clientY: number };
const GESTURE_EVENTS = ['gesturestart', 'gesturechange', 'gestureend'] as const;
const WHEEL_RATE = 0.0015, PINCH_RATE = 0.01;
/** Half the square a finger needs round a resize handle; nothing floating may cover it. */
const HANDLE_TOUCH_ZONE = 22;
/** One wheel notch or pinch step never crosses more than a stop of zoom, however big the burst. */
const zoomStep = (amount: number) => Math.exp(Math.max(-1, Math.min(1, amount)));

/** The floor below that shows through the floor plan of this one while it is built (`floorBelow`); none on the ceiling plan. */
const referenceFor = (s: EditorState): Level | undefined =>
  s.planSurface === 'floor' && s.scene ? floorBelow(s.scene.levels, s.activeLevelIndex, s.tool === 'wall' || s.tool === 'room') : undefined;

/** The stairwells in the surface on the plan: in the floor over the flights of the floor below, in the ceiling over this floor's. */
const wellsFor = (s: EditorState) => {
  const levels = store.presentedScene?.levels ?? s.scene?.levels ?? [];
  return s.planSurface === 'ceiling' ? wellsUp(levels, s.activeLevelIndex) : wellsDown(levels, s.activeLevelIndex);
};

const planState = (s: EditorState) => ({ scene: s.scene, propertiesPreview: s.propertiesPreview, projectId: s.projectId, activeLevelIndex: s.activeLevelIndex, mode: s.mode,
    showDimensions: s.showDimensions, planSurface: s.planSurface, selection: s.selection, materialTransformPreview: s.materialTransformPreview,
    isDragging: s.isDragging, resizeMode: s.resizeMode, tool: s.tool, wallThickness: s.wallThickness, planScale: s.planScale });

/** What a press on `target` picks: a corner or the middle of a wall picks the wall, a handle its item, an opening's point the opening. */
const selectionOfHit = (target: HitTarget): Selection => {
  switch (target?.kind) {
    case 'item': case 'rotate': case 'resize': return { kind: 'item', id: target.id };
    case 'opening': case 'opening-resize': return { kind: 'opening', id: target.id };
    case 'wall': return { kind: 'wall', id: target.id, side: target.side };
    case 'wall-mid': return { kind: 'wall', id: target.id, side: 'front' };
    case 'vertex': return { kind: 'wall', id: target.wallId, side: 'front' };
    case 'room': case 'room-move': return { kind: 'room', index: target.index };
    default: return null;
  }
};
/** A handle or a corner under the pointer keeps its own press: a label there is not typed over. */
const labelBlocked = (target: HitTarget) => !!target && ['vertex', 'room-move', 'rotate', 'resize', 'opening-resize', 'item', 'opening'].includes(target.kind);

export interface PlanHandle {
  participantViewport(): PresenceViewport | null;
  applyParticipantViewport(viewport: PresenceViewport): boolean;
  cancelInteraction(): void;
  worldPointAt(clientX: number, clientY: number): { x: number; y: number; z: number } | null;
  onFrame(listener: () => void): () => void;
  startOpeningDrag(kind: OpeningKind): void;
  /** Where a plan point currently sits on screen, in client coordinates. */
  screenPointAt(point: Vec2, bounds?: DOMRect): ScreenPoint | null;
  /**
   * Pan the plan until `point` sits inside `area`, and with `outline` until all of the outline does, `point` in the
   * middle, as far as it fits. Returns whether the view moved.
   */
  revealPoint(point: Vec2, area: ScreenRect, outline?: readonly Vec2[]): boolean;
  /** Where the plan currently sits, for storing the view the project was left on. */
  viewport(): Viewport | null;
  /** The plan canvas, for reading the frame on screen. */
  canvasElement(): HTMLCanvasElement | null;
  /** The height of an item's loaded surface under plan points, for landing a dropped piece on it. */
  surfaceHeight: SurfaceProbe;
  /** A context press at a client point: any press in progress let go, what is there selected (`contextSelection`) unless a viewer's. */
  contextPressAt(clientX: number, clientY: number): ContextPressResult;
}

export function PlanEditor({ getFurnitureLayer, viewports, onNavigationIntent, ref }: { getFurnitureLayer?: () => PlanFurnitureLayer | null; viewports?: Map<string, Viewport>; onNavigationIntent?: () => void; ref?: Ref<PlanHandle> }) {
  const navigationIntent = useRef(onNavigationIntent); navigationIntent.current = onNavigationIntent;
  const state = useStore(planState);
  const drawnState = useRef<ReturnType<typeof planState> | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const viewRef = useRef<PlanView | null>(null);
  const frameListeners = useRef(new Set<() => void>());
  const layerGetter = useRef(getFurnitureLayer); layerGetter.current = getFurnitureLayer;
  const layerRef = useRef<PlanFurnitureLayer | null>(null);
  const unsubscribeLayer = useRef<(() => void) | null>(null);
  const dragRef = useRef<Drag | null>(null);
  const pointerRef = useRef<number | null>(null);
  const lastInput = useRef<PointerInput | null>(null);
  const hoverRef = useRef<HitTarget>(null);
  /** A wall's length or a room's area under the pointer (lit, pointer cursor), and the one a press began on. */
  const labelHover = useRef<string | null>(null), pressLabel = useRef<DimensionLabel | null>(null);
  /** The label being typed over (`DimensionInput`), at its middle in the window's pixels. */
  const [typing, setTyping] = useState<{ label: DimensionLabel; at: { x: number; y: number } } | null>(null);
  const typingRef = useRef(typing); typingRef.current = typing;
  /** The size this plan put into the shared resize highlight, so it takes back only its own. */
  const planResizeHot = useRef<ResizeFace['axis'] | null>(null);
  const appliedCursor = useRef('');
  const startRef = useRef({ x: 0, y: 0 });
  const movedRef = useRef(false);
  const fittedFor = useRef<string | null>(null);
  const savedViews = useRef(viewports ?? new Map<string, Viewport>());
  /** The view this project was last drawn at — this session's, else the one it was left on (never for an invitation: that one is the guest's own copy's). */
  const storedViewport = useCallback((projectId: string): Viewport | null =>
    savedViews.current.get(projectId) ?? (store.get().edit !== 'all' ? null : readProjectView(projectId)?.plan) ?? null, []);
  const renderFrame = useRef<number | null>(null), flushingFrame = useRef(false);
  const revealFrame = useRef<number | null>(null);
  const pendingInput = useRef<PointerInput | null>(null);
  const applyInput = useRef<(input: PointerInput) => boolean>(() => false);
  const drawRequested = useRef(false);
  const touches = useRef(new Map<number, { x: number; y: number }>());
  const navigating = useRef(false), coarse = useRef(false);
  const pinch = useRef<{ distance: number; scale: number; anchor: Vec2 } | null>(null);
  /** A Safari trackpad pinch in flight, and how far it has spread since it began. */
  const gesturing = useRef(false), gestureSpread = useRef(1);
  const draftRef = useRef<PlanDraft | null>(null);
  /** The ruler's line being pulled. */
  const measureRef = useRef<{ from: Vec2; to: Vec2 } | null>(null);
  const guidesRef = useRef<PlanGuide[]>([]);
  const capturesRef = useRef<SnapCapture[]>([]);
  const messageRef = useRef<string | null>(null);
  /** Where the next wall of a chain starts: the end of the one just drawn. Escape, or leaving the tool, ends it. */
  const chainRef = useRef<Vec2 | null>(null);
  /** What the point snap held last frame, so a capture persists through the hand's tremble. */
  const snapCaptureRef = useRef<PointSnapCapture | null>(null);
  const anchorRef = useRef<{ point: Vec2; snapped: boolean } | null>(null);
  const invalidRef = useRef<{ quads: Vec2[][]; handle?: Vec2 } | null>(null);
  const snapRef = useRef<{ wallId?: string; handle?: Vec2 } | null>(null);
  /** True while this editor commits a wall itself, so its own document change does not end the chain. */
  const committingRef = useRef(false);
  const selectionFrameKey = useRef('');

  const draw = useCallback(() => {
    const view = viewRef.current, canvas = canvasRef.current, s = store.get();
    if (!view || !s.scene || !store.level || !canvas || s.mode !== 'plan') return;
    if (!layerRef.current) {
      const layer = layerGetter.current?.();
      if (layer) { layerRef.current = layer; view.setFurnitureLayer(layer); unsubscribeLayer.current = layer.subscribe(() => { view.invalidateFurniture(); render(); }); }
    }
    if (s.selection?.kind !== 'opening' && s.selection?.kind !== 'room') selectionFrameKey.current = '';
    if ((s.selection?.kind === 'opening' || s.selection?.kind === 'room') && s.tool === 'navigate' && !dragRef.current && !navigating.current && !s.isDragging) {
      const inspector = canvas.closest('.editor')?.querySelector('.item-inspector')?.getBoundingClientRect();
      const level = store.presentedLevel!, selection = s.selection;
      const opening = selection.kind === 'opening' ? level.openings.find(value => value.id === selection.id) : undefined;
      const wall = level.walls.find(value => value.id === opening?.wallId);
      const room = selection.kind === 'room' ? level.rooms.find(value => value.index === selection.index) : undefined;
      const handlesForView = () => room ? [view.toScreen(roomMoveHandlePoint(room))] : opening && wall ? view.openingResizeHandles(opening, wall) : [];
      if (inspector && (room || opening && wall)) {
        const bounds = canvas.getBoundingClientRect();
        const key = JSON.stringify([s.projectId, s.activeLevelIndex, selection, room?.polygon, opening?.width, opening?.offset, wall?.start, wall?.end, bounds.width, bounds.height, inspector.width, inspector.height]);
        if (selectionFrameKey.current !== key) {
          selectionFrameKey.current = key;
          const handles = handlesForView();
          const obscured = handles.some(handle => handle.x + bounds.left + 22 > inspector.left - 12 && handle.x + bounds.left - 22 < inspector.right
            && handle.y + bounds.top + 22 > inspector.top && handle.y + bounds.top - 22 < inspector.bottom);
          const minX = bounds.left + 26, maxX = Math.min(bounds.right - 26, inspector.left - 38);
          if (obscured && maxX > minX) {
            const span = Math.max(...handles.map(h => h.x)) - Math.min(...handles.map(h => h.x));
            if (span > maxX - minX) view.viewport.scale *= span / (maxX - minX);
            const framed = handlesForView().map(handle => handle.x + bounds.left);
            const left = Math.min(...framed), right = Math.max(...framed);
            view.panBy(Math.max(minX - left, Math.min(0, maxX - right)), 0);
          }
        }
      }
    }
    const typed = typingRef.current;
    view.draw(store.presentedLevel!, s.selection, { dimensions: s.showDimensions || !!s.planScale, scaleMode: !!s.planScale,
      labelFocus: typed ? dimensionLabelKey(typed.label) : labelHover.current,
      surface: s.planSurface === 'ceiling' ? 'ceiling' : 'floor', reference: referenceFor(s), wells: wellsFor(s), draft: draftRef.current,
      guides: guidesRef.current, message: messageRef.current, hover: dragRef.current ? null : hoverRef.current,
      footprints: dragRef.current?.kind==='resize'?undefined:dragRef.current?.footprints, dragging: s.isDragging,
      // The obstruction outline belongs to the act of placing, not to the selection: a pan
      // or a wall edit with furniture selected must not turn the room red.
      placing: s.isDragging && ['move', 'rotate', 'resize'].includes(dragRef.current?.kind ?? ''),
      openingResize: dragRef.current?.kind === 'opening-resize' ? { id: dragRef.current.id, side: dragRef.current.side } : undefined,
      movingOpening: s.isDragging && dragRef.current?.kind === 'opening',
      openingHandles: s.tool === 'navigate' && s.edit !== 'items',
      materialPreview:s.materialTransformPreview,resize:s.resizeMode,resizeFace:dragRef.current?.kind==='resize'?dragRef.current.face:undefined,resizeHot:resizeHot(),
      resizeStart:dragRef.current?.kind==='resize'?dragRef.current.base.items.find(item=>dragRef.current?.kind==='resize'&&item.id===dragRef.current.id):undefined,
      // The panels over the plan, in its own pixels: a size beside a handle goes where none covers it.
      resizeObstacles:s.resizeMode?(()=>{const rect=canvas.getBoundingClientRect();return chromeRects().map(panel=>({...panel,x:panel.x-rect.left,y:panel.y-rect.top}));})():undefined,
      anchor: anchorRef.current, chain: s.tool === 'wall' ? chainRef.current : null, invalid: invalidRef.current, snap: snapRef.current,
      measures: s.tool === 'measure' ? [...s.measurements, ...(measureRef.current ? [measureRef.current] : [])] : undefined,
      accent: s.tool !== 'navigate' && s.tool !== 'measure' || ['draw', 'vertex', 'wall', 'room', 'split', 'opening', 'insert'].includes(dragRef.current?.kind ?? '') });
    drawnState.current = planState(s);
    canvas.dataset.gesture = dragRef.current?.kind ?? 'idle';
    canvas.dataset.reference = String(view.drawnReferenceWalls);
    canvas.dataset.wells = String(view.drawnWells);
    canvas.dataset.hover = hoverRef.current?.kind ?? 'none';
    canvas.dataset.guides = String(guidesRef.current.length);
    canvas.dataset.openingMarkers = view.drawnOpeningMarkers.map(marker => marker.length.toFixed(3)).join(',');
    canvas.dataset.snapTargets = capturesRef.current.map(capture => capture.target).join(',');
    const wallDraft = draftRef.current && !draftRef.current.kind ? draftRef.current : null;
    canvas.dataset.chain = s.tool === 'wall' && chainRef.current ? 'active' : 'none';
    canvas.dataset.thickness = String(Math.round(s.wallThickness * 1000));
    canvas.dataset.anchor = wallDraft ? (wallDraft.snapped ? 'snapped' : 'free') : anchorRef.current ? (anchorRef.current.snapped ? 'snapped' : 'free') : 'none';
    canvas.dataset.axis = wallDraft && !samePoint(wallDraft.start, wallDraft.end) ? (standardAngle(wallDraft.start, wallDraft.end) ? 'snapped' : 'free') : 'none';
    const frame = s.isDragging || dragRef.current?.kind === 'pan' ? null : view.selectionFrame(store.presentedLevel!, s.selection);
    // A selected opening's resize handles keep their touch zone: the context bar goes round it, never over it.
    const selectedOpening = frame && s.selection?.kind === 'opening' && s.tool === 'navigate' ? store.presentedLevel!.openings.find(o => s.selection?.kind === 'opening' && o.id === s.selection.id) : undefined;
    const openingWall = selectedOpening && store.presentedLevel!.walls.find(w => w.id === selectedOpening.wallId);
    // So do an item's resize handles in resize mode.
    const resizedItem = frame && s.resizeMode && s.selection?.kind === 'item' ? store.presentedLevel!.items.find(item => s.selection?.kind === 'item' && item.id === s.selection.id) : undefined;
    const canvasRect = (selectedOpening && openingWall) || resizedItem ? canvas.getBoundingClientRect() : null;
    const handles = selectedOpening && openingWall ? view.openingResizeHandles(selectedOpening, openingWall) : resizedItem ? view.resizeHandles(resizedItem) : [];
    const keepOut = canvasRect && handles.length ? [...handles
      .map(handle => ({ x: canvasRect.left + handle.x - HANDLE_TOUCH_ZONE, y: canvasRect.top + handle.y - HANDLE_TOUCH_ZONE, width: 2 * HANDLE_TOUCH_ZONE, height: 2 * HANDLE_TOUCH_ZONE })),
      ...(resizedItem ? view.drawnResizeLabels.map(label => ({ ...label, x: canvasRect.left + label.x, y: canvasRect.top + label.y })) : [])] : undefined;
    window.dispatchEvent(new CustomEvent('hp:selection-frame', { detail: frame && keepOut ? { ...frame, keepOut } : frame }));
    const hover = hoverRef.current, drag = dragRef.current, presented = store.presentedLevel!, degrees = 180 / Math.PI;
    // A resize handle under the pointer lights its size in the resize bar too, and the cursor takes its axis.
    const hoverAxis = hover?.kind === 'resize' && s.resizeMode ? hover.axis : null;
    if (hoverAxis !== planResizeHot.current) { if (planResizeHot.current) releaseResizeHot(planResizeHot.current); if (hoverAxis) setResizeHot(hoverAxis); planResizeHot.current = hoverAxis; }
    const resizing = drag?.kind === 'resize' ? { id: drag.id, axis: drag.face.axis } : hover?.kind === 'resize' && !s.isDragging ? hover : null;
    const resized = resizing && presented.items.find(value => value.id === resizing.id);
    const openingResize = s.tool !== 'navigate' ? null : drag?.kind === 'opening-resize' ? drag : hover?.kind === 'opening-resize' ? hover : null;
    const resizedOpening = openingResize && presented.openings.find(opening => opening.id === openingResize.id);
    const resizedWall = resizedOpening && presented.walls.find(wall => wall.id === resizedOpening.wallId);
    const turning = drag?.kind === 'rotate' ? drag.id : hover?.kind === 'rotate' ? hover.id : null;
    const turned = turning !== null ? presented.items.find(item => item.id === turning) : undefined, handle = turned && rotateHandlePoint(turned);
    const cursor = cursorStyle(planCursor({
      pan: panOnly() ? 'ready' : drag?.kind === 'pan' && drag.space ? 'held' : null,
      tool: s.tool === 'navigate' || s.tool === 'measure' ? s.tool : 'construct',
      drag: s.isDragging ? drag?.kind ?? 'pan' : null, alt: !!lastInput.current?.altKey, refused: !!invalidRef.current,
      hover: hover?.kind ?? null, label: !!labelHover.current,
      pressOnly: !!hover && (s.edit === 'items' && architectureGrab(hover, presented) || hover.kind === 'item' && !!presented.items.find(item => item.id === hover.id)?.locked),
      selectedRoom: hover?.kind === 'room' && s.selection?.kind === 'room' && s.selection.index === hover.index && s.edit !== 'items',
      resizeAngle: resized ? (resized.rotation + (resizing.axis === 'w' ? Math.PI / 2 : 0)) * degrees : undefined,
      openingAngle: resizedWall ? Math.atan2(resizedWall.dir.z, resizedWall.dir.x) * degrees : undefined,
      rotateAngle: turned && handle ? rotateGlyphAngle(turned.position.x, turned.position.z, handle.x, handle.z) : undefined }));
    if (appliedCursor.current !== cursor) { appliedCursor.current = cursor; canvas.style.cursor = cursor; }
    const pointer = coarse.current ? null : lastInput.current, dragged = pointer && s.isDragging ? drag : null;
    const chip = pointer && planChip({ draft: draftRef.current,
      wall: dragged?.kind === 'vertex' || dragged?.kind === 'wall' ? presented.walls.find(wall => wall.id === dragged.id) : undefined,
      item: dragged?.kind === 'resize' && resized ? itemChip('resize', resized, dragged.face.axis) : dragged?.kind === 'rotate' && turned ? itemChip('rotate', turned) : undefined });
    if (chip && pointer) showGestureChip(chipText(chip), pointer.clientX, pointer.clientY); else hideGestureChip();
    publishViewZoom(zoomPercent(view.viewport.scale));
    for (const listener of frameListeners.current) listener();
  }, []);

  const updatePinch = useCallback(() => {
    const view = viewRef.current, canvas = canvasRef.current, gesture = pinch.current;
    if (!view || !canvas || !gesture || touches.current.size < 2) return;
    const [a, b] = [...touches.current.values()], rect = canvas.getBoundingClientRect();
    const x = (a.x + b.x) / 2 - rect.left, y = (a.y + b.y) / 2 - rect.top;
    const distance = Math.max(1, Math.hypot(a.x - b.x, a.y - b.y));
    view.zoomAt(x, y, gesture.scale * gesture.distance / distance / view.viewport.scale);
    const after = view.toPlan(x, y);
    view.viewport.centre.x += gesture.anchor.x - after.x; view.viewport.centre.z += gesture.anchor.z - after.z;
  }, []);
  const render = useCallback((needsDraw = true) => {
    drawRequested.current ||= needsDraw;
    if (renderFrame.current !== null || flushingFrame.current) return;
    renderFrame.current = requestAnimationFrame(() => {
      renderFrame.current = null; flushingFrame.current = true;
      try {
        const input = pendingInput.current; pendingInput.current = null;
        if (navigating.current) { updatePinch(); drawRequested.current = true; }
        else if (input) drawRequested.current = applyInput.current(input) || drawRequested.current;
        if (drawRequested.current) { drawRequested.current = false; draw(); }
      } finally { flushingFrame.current = false; }
    });
  }, [draw, updatePinch]);

  const clearHoverPreview = useCallback(() => {
    pendingInput.current = null; lastInput.current = null;
    draftRef.current = null; hoverRef.current = null; messageRef.current = null;
    anchorRef.current = null; guidesRef.current = []; snapCaptureRef.current = null;
  }, []);

  const stopReveal = useCallback(() => {
    if (revealFrame.current !== null) cancelAnimationFrame(revealFrame.current);
    revealFrame.current = null;
  }, []);
  const screenPointAt = useCallback((point: Vec2, bounds?: DOMRect): ScreenPoint | null => {
    const view = viewRef.current, canvas = canvasRef.current;
    if (!view || !canvas) return null;
    const rect = bounds ?? canvas.getBoundingClientRect(), at = view.toScreen(point);
    return { x: rect.left + at.x, y: rect.top + at.y };
  }, []);

  const cancel = useCallback((releaseCapture = true) => {
    const drag = dragRef.current;
    stopReveal();
    pendingInput.current = null; lastInput.current = null; navigating.current = false; pinch.current = null;
    const touchIds = [...touches.current.keys()]; touches.current.clear();
    dragRef.current = null; draftRef.current = null; measureRef.current = null; guidesRef.current = []; capturesRef.current = [];
    hoverRef.current = null; messageRef.current = null;
    anchorRef.current = null; invalidRef.current = null; snapRef.current = null; snapCaptureRef.current = null;
    store.cancelStroke();
    if (['door', 'window', 'arch', 'room'].includes(store.get().tool)) store.set({ tool: 'navigate' });
    if (drag && drag.projectId === store.get().projectId && drag.levelIndex === store.get().activeLevelIndex && (!('base' in drag) || drag.base === store.level || (drag.kind !== 'insert' && drag.kind !== 'room'))) store.select(drag.initialSelection, drag.initialAnchor ?? undefined);
    const canvas = canvasRef.current, pointer = pointerRef.current;
    pointerRef.current = null;
    if (releaseCapture) {
      if (canvas && pointer !== null && canvas.hasPointerCapture(pointer)) canvas.releasePointerCapture(pointer);
      for (const id of touchIds) if (canvas?.hasPointerCapture(id)) canvas.releasePointerCapture(id);
    }
    render();
  }, [render, stopReveal]);

  useImperativeHandle(ref, () => ({ cancelInteraction: cancel, screenPointAt,
    contextPressAt: (x, y) => {
      const canvas = canvasRef.current, view = viewRef.current, level = store.level;
      if (!canvas || !view || !level) return { point: null, decided: false };
      cancel();
      const rect = canvas.getBoundingClientRect(), px = x - rect.left, py = y - rect.top, point = view.toPlan(px, py);
      if (store.get().edit === 'none') return { point, decided: false };
      const next = contextSelection(store.get().selection, selectionOfHit(hit(view, px, py, level)));
      store.select(next, next ? { x, y } : null);
      render();
      return { point, decided: JSON.stringify(store.get().selection) === JSON.stringify(next) };
    },
    worldPointAt: (x, y) => {
      const canvas = canvasRef.current, view = viewRef.current;
      if (!canvas || !view) return null;
      const rect = canvas.getBoundingClientRect();
      if (!rect.width || !rect.height || x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) return null;
      const point = view.toPlan(x - rect.left, y - rect.top);
      return { ...point, y: store.get().planSurface === 'ceiling' ? store.level?.height ?? 0 : 0 };
    },
    onFrame: listener => { frameListeners.current.add(listener); return () => { frameListeners.current.delete(listener); }; },
    surfaceHeight: Object.assign((item: Item, points: readonly Vec2[]) => viewRef.current?.surfaceHeight(item, points) ?? null,
      { layers: (item: Item, points: readonly Vec2[]) => viewRef.current?.surfaceHeight.layers?.(item, points) ?? null }), startOpeningDrag: (openingKind) => {
    cancel(); chainRef.current = null;
    const s = store.get(), base = store.level;
    if (!base) return;
    store.set({ tool: openingKind });
    dragRef.current = { kind: 'insert', openingKind, base, projectId: s.projectId, levelIndex: s.activeLevelIndex, floor: base,
      initialSelection: s.selection, initialAnchor: s.selectionAnchor, footprints: new Map() };
  }, revealPoint: (point, area, outline) => {
    const view = viewRef.current, at = screenPointAt(point);
    if (!view || !at || withinArea(at, area, REVEAL_INSET) && (outline ?? []).every(corner => withinArea(screenPointAt(corner), area))) return false;
    const centre = areaCentre(area), scale = view.viewport.scale;
    const fromX = view.viewport.centre.x, fromZ = view.viewport.centre.z;
    const toX = fromX - (centre.x - at.x) * scale, toZ = fromZ - (centre.y - at.y) * scale;
    stopReveal();
    const reduced = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
    const started = performance.now();
    const step = () => {
      revealFrame.current = null;
      if (!viewRef.current) return;
      const t = reduced ? 1 : Math.min(1, (performance.now() - started) / 180), eased = 1 - (1 - t) ** 3;
      view.viewport.centre.x = fromX + (toX - fromX) * eased;
      view.viewport.centre.z = fromZ + (toZ - fromZ) * eased;
      render();
      if (t < 1) revealFrame.current = requestAnimationFrame(step);
    };
    step();
    return true;
  }, participantViewport: () => {
    const view = viewRef.current, canvas = canvasRef.current, current = store.get();
    if (!view || !canvas || fittedFor.current !== current.projectId || current.mode !== 'plan' || current.planSurface === 'wall') return null;
    return capturePlanViewport(view.viewport, canvas.clientWidth, canvas.clientHeight, current.planSurface);
  }, applyParticipantViewport: viewport => {
    const view = viewRef.current, canvas = canvasRef.current, current = store.get(), drawn = drawnState.current;
    if (!view || !canvas || fittedFor.current !== current.projectId || drawn?.activeLevelIndex !== current.activeLevelIndex
      || drawn.planSurface !== current.planSurface || viewport.kind !== current.planSurface
      || viewport.kind !== 'floor' && viewport.kind !== 'ceiling') return false;
    const next = applyPlanViewport(viewport, canvas.clientWidth, canvas.clientHeight);
    if (!next) return false;
    stopReveal(); view.viewport = next; render(); return true;
  }, viewport: () => {
    const view = viewRef.current;
    return view && view.hasSize() ? { scale: view.viewport.scale, centre: { ...view.viewport.centre } } : null;
  }, canvasElement: () => canvasRef.current }), [cancel, render, screenPointAt, stopReveal]);

  useEffect(() => {
    let presented = store.presentedLevel;
    return store.subscribe(() => {
      const next = store.presentedLevel;
      if (next === presented) return;
      presented = next; viewRef.current?.invalidateFurniture(); render();
    });
  }, [render]);
  // A size lit in the resize bar lights its faces on the plan.
  useEffect(() => subscribeResizeHot(() => render()), [render]);
  useEffect(() => hideGestureChip, []);
  // A flight's model measured: its stairwell follows the model as drawn.
  useEffect(() => {
    let drawn = wellsKey(wellsFor(store.get()));
    return subscribeModelBounds(() => {
      const next = wellsKey(wellsFor(store.get()));
      if (next !== drawn) { drawn = next; render(); }
    });
  }, [render]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const view = new PlanView(canvas, render); viewRef.current = view; fittedFor.current = null;
    (canvas as unknown as { __planView?: PlanView }).__planView = view;
    const observer = new ResizeObserver(() => {
      view.resize();
      if (!dragRef.current) clearHoverPreview();
      if (fittedFor.current === null && view.hasSize() && store.level) {
        const projectId = store.get().projectId, saved = storedViewport(projectId);
        if (saved) view.viewport = { scale: saved.scale, centre: { ...saved.centre } }; else view.fit(store.level, referenceFor(store.get()));
        fittedFor.current = projectId;
      }
      render();
    });
    observer.observe(canvas);
    const fit = () => { if (!dragRef.current && store.level) { navigationIntent.current?.(); clearHoverPreview(); view.fit(store.level, referenceFor(store.get())); render(); } };
    window.addEventListener('hp:fit-plan', fit);
    // The minimap's "go to room" in 2D: centre the plan on the room, zoomed so it fills the view.
    const focus = (event: Event) => { const detail = (event as CustomEvent<{ centre: Vec2; extent: number }>).detail; if (!dragRef.current && detail) { navigationIntent.current?.(); clearHoverPreview(); view.focus(detail.centre, detail.extent); render(); } };
    window.addEventListener('hp:pan-plan', focus);
    return () => {
      if (fittedFor.current !== null) savedViews.current.set(fittedFor.current, { scale: view.viewport.scale, centre: { ...view.viewport.centre } });
      if (renderFrame.current !== null) cancelAnimationFrame(renderFrame.current);
      if (revealFrame.current !== null) cancelAnimationFrame(revealFrame.current);
      revealFrame.current = null;
      renderFrame.current = null; pendingInput.current = null;
      observer.disconnect(); view.dispose(); window.removeEventListener('hp:fit-plan', fit); window.removeEventListener('hp:pan-plan', focus);
      unsubscribeLayer.current?.(); unsubscribeLayer.current = null; layerRef.current = null;
      if (dragRef.current) { const drag = dragRef.current; store.cancelStroke(); if (drag.projectId === store.get().projectId && drag.levelIndex === store.get().activeLevelIndex && drag.kind !== 'insert' && drag.kind !== 'room') store.select(drag.initialSelection, drag.initialAnchor ?? undefined); }
      if (navigating.current) store.set({ isDragging: false });
      if (['door', 'window', 'arch', 'room'].includes(store.get().tool)) store.set({ tool: 'navigate' });
      touches.current.clear(); navigating.current = false; pinch.current = null; dragRef.current = null; viewRef.current = null;
      window.dispatchEvent(new CustomEvent('hp:selection-frame', { detail: null }));
    };
  }, [render, clearHoverPreview, storedViewport]);

  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    if(dragRef.current?.kind==='resize'&&!state.resizeMode) cancel();
    const previous = drawnState.current;
    if (previous && (previous.projectId !== state.projectId || previous.activeLevelIndex !== state.activeLevelIndex)) {
      if (dragRef.current || navigating.current) cancel();
      chainRef.current = null; view.invalidateFurniture();
    }
    let changedView = false;
    if (state.scene && store.level && fittedFor.current !== state.projectId) {
      if (fittedFor.current !== null) savedViews.current.set(fittedFor.current, { scale: view.viewport.scale, centre: { ...view.viewport.centre } });
      if (dragRef.current || navigating.current) cancel();
      view.resize(); view.invalidateFurniture();
      if (view.hasSize()) {
        const saved = storedViewport(state.projectId);
        if (saved) view.viewport = { scale: saved.scale, centre: { ...saved.centre } }; else view.fit(store.level, referenceFor(store.get()));
        fittedFor.current = state.projectId; changedView = true;
      }
    }
    if (changedView || !shallowEqual(drawnState.current, state)) render();
  }, [state, render, cancel, storedViewport]);

  useEffect(() => {
    let previous = store.get();
    return store.subscribe(() => {
      const next = store.get(), before = previous, drag = dragRef.current;
      previous = next;
      if ((drag || navigating.current) && (before.projectId !== next.projectId || before.activeLevelIndex !== next.activeLevelIndex || before.mode !== next.mode)) { chainRef.current = null; cancel(); }
      else if (drag && (drag.kind === 'insert' || drag.kind === 'room') && !constructionInScope(drag)) cancel();
      else if (drag && planDragLost(drag.kind, before, next)) cancel();
      else if (drag?.kind === 'room-move' && (next.selection?.kind !== 'room' || next.selection.index !== drag.index || before.tool !== next.tool)) cancel();
      else if (drag?.kind === 'opening-resize' && (before.selection !== next.selection || before.tool !== next.tool)) cancel();
      else if (!drag && (before.scene !== next.scene || before.propertiesPreview !== next.propertiesPreview || before.projectId !== next.projectId || before.activeLevelIndex !== next.activeLevelIndex || before.mode !== next.mode || before.tool !== next.tool)) {
        // A chain outlives its own commits; anything else that rewrites the plan, or leaves the tool, ends it.
        if (before.tool !== next.tool || before.mode !== next.mode || before.projectId !== next.projectId || before.activeLevelIndex !== next.activeLevelIndex
          || (before.scene !== next.scene && !committingRef.current)) chainRef.current = null;
        clearHoverPreview(); render();
      }
    });
  }, [cancel, clearHoverPreview, render]);

  const hit = useCallback((view: PlanView, x: number, y: number, level: Level): HitTarget => {
    const s = store.get();
    return view.hit(x, y, level, s.selection, s.planSurface === 'ceiling' ? 'ceiling' : 'floor',s.resizeMode,coarse.current,s.tool === 'navigate' && s.edit !== 'items');
  }, []);

  /** The wall a press or a drag proposes from `start` to the snapped anchor: its validity, guides and the reason it fails. */
  const previewWall = useCallback((base: Level, start: Vec2, snapped: PointSnapResult) => {
    // Shorter than a wall can be is not yet a wall, not a refusal: the anchor stays calm and nothing complains.
    const pending = Math.hypot(snapped.point.x - start.x, snapped.point.z - start.z) < MIN_WALL;
    const thickness = store.get().wallThickness;
    const result = pending ? null : addWall(base, start, snapped.point, thickness);
    draftRef.current = { start, end: snapped.point, valid: !!result?.level, snapped: !!snapped.target, pending, thickness };
    guidesRef.current = !result || result.level ? snapped.guides : []; messageRef.current = result?.error ?? null;
  }, []);

  const onPointerDown = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    const view = viewRef.current, level = store.level;
    // The right button always pans, whatever tool is armed and whatever lies under it — the same
    // spare hand the 3D view gives for looking around, and it never changes the selection.
    const secondary = e.button === 2;
    if (!view || !level || (e.button !== 0 && !secondary)) return;
    navigationIntent.current?.();
    stopReveal();   // the user takes the view back the moment they touch the plan
    coarse.current = e.pointerType === 'touch' || (typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches);
    if (e.pointerType === 'touch') {
      touches.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (touches.current.size >= 2) {
        const tracked = new Map(touches.current); cancel(false); touches.current = tracked; navigating.current = true;
        const [a, b] = [...tracked.values()], rect = e.currentTarget.getBoundingClientRect();
        pinch.current = { distance: Math.max(1, Math.hypot(a.x - b.x, a.y - b.y)), scale: view.viewport.scale,
          anchor: view.toPlan((a.x + b.x) / 2 - rect.left, (a.y + b.y) / 2 - rect.top) };
        for (const id of tracked.keys()) e.currentTarget.setPointerCapture(id);
        store.set({ isDragging: true, selectionAnchor: null }); e.preventDefault(); render(); return;
      }
      if (navigating.current) return;
    }
    if (dragRef.current || navigating.current) return;
    if (typingRef.current) setTyping(null);
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left, y = e.clientY - rect.top;
    const presentedTarget = hit(view, x, y, store.presentedLevel ?? level);
    // A tap on a wall's length or a room's area types over it; pressed and dragged, it does what the press would do.
    pressLabel.current = !secondary && !e.shiftKey && store.get().tool === 'navigate' && !store.get().resizeMode && !panOnly() && !itemsOnly()
      && !labelBlocked(presentedTarget) ? view.labelAt(x, y, coarse.current) : null;
    store.clearPropertiesPreview();
    e.preventDefault();
    // A pointer the browser does not consider active (synthetic, or already released) must not abort the press.
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* keep the gesture */ }
    pointerRef.current = e.pointerId;
    lastInput.current = pointerInput(e);
    startRef.current = { x, y }; movedRef.current = false; messageRef.current = null; capturesRef.current = [];
    const s = store.get(), anchor = { x: e.clientX, y: e.clientY };
    const common: DragBase = { projectId: s.projectId, levelIndex: s.activeLevelIndex, floor: level, initialSelection: s.selection, initialAnchor: s.selectionAnchor, footprints: view.captureFootprints(level) };
    if (secondary) { dragRef.current = { ...common, kind: 'pan', lastX: x, lastY: y, secondary: true }; render(); return; }
    // Figma's Space: a press anywhere pans, whatever tool is armed and whatever lies under the pointer.
    if (panOnly()) { dragRef.current = { ...common, kind: 'pan', lastX: x, lastY: y, space: true }; render(); return; }
    if (s.tool === 'room') {
      const start = snapVertexPoint(view.toPlan(x, y), level, { scale: view.viewport.scale, disabled: e.ctrlKey || e.metaKey }).point;
      dragRef.current = { ...common, kind: 'room', start, base: level }; draftRef.current = { kind: 'room', start, end: start, valid: false }; render(); return;
    }
    if (s.tool === 'measure') {
      // The ruler starts where the press is, held by a wall's corner or body as a wall's end would be.
      const start = snapVertexPoint(view.toPlan(x, y), level, { scale: view.viewport.scale, disabled: e.ctrlKey || e.metaKey, segments: true }).point;
      dragRef.current = { ...common, kind: 'measure', start, base: level }; measureRef.current = { from: start, to: start }; render(); return;
    }
    if (s.tool === 'door' || s.tool === 'window' || s.tool === 'arch') {
      dragRef.current = { ...common, kind: 'insert', openingKind: s.tool, base: level };
      draftRef.current = null;
      pendingInput.current = pointerInput(e); render(); return;
    }
    if (s.tool === 'wall') {
      // A chain in progress already owns the start: the press only places the moving end.
      const raw = view.toPlan(x, y), chain = chainRef.current;
      const snapped = snapVertexPoint(raw, level, { scale: view.viewport.scale, disabled: e.ctrlKey || e.metaKey, anchor: chain ?? undefined, previous: snapCaptureRef.current });
      snapCaptureRef.current = snapped.capture; anchorRef.current = null;
      const start = chain ?? snapped.point;
      dragRef.current = { ...common, kind: 'draw', start, base: level, chain: !!chain, anchor: raw };
      if (chain) previewWall(level, chain, snapped);
      else { draftRef.current = { start, end: start, valid: false, pending: true, snapped: !!snapped.target, thickness: s.wallThickness }; guidesRef.current = snapped.guides; }
      render(); return;
    }
    const target = presentedTarget?.kind === 'opening-resize' ? presentedTarget : hit(view, x, y, level);
    const gathered = selectedItemIds(s.selection);
    if (e.shiftKey && (target?.kind === 'item' ? !gathered.includes(target.id) : gathered.length > 0)) {
      // Shift gathers items: a press on another item adds it to the selection, and a press anywhere
      // else leaves the gathered items alone. Dragged, the press still pans. A Shift press on an item
      // already selected moves it as usual (along one axis) and, released in place, takes it out.
      if (target?.kind === 'item') store.select(toggleItemSelection(s.selection, target.id), anchor);
      dragRef.current = { ...common, kind: 'pan', lastX: x, lastY: y, gathered: true };
    } else if (s.edit === 'items' && (architectureGrab(target, level) || (target?.kind === 'room' && s.selection?.kind === 'room' && s.selection.index === target.index))) {
      const picked = selectionOfHit(target);
      if (picked?.kind === 'wall' || picked?.kind === 'opening') store.select(picked, anchor);
      dragRef.current = { ...common, kind: 'pan', lastX: x, lastY: y, gathered: true, toggles: target?.kind !== 'resize' && target?.kind !== 'rotate' };
    } else if (target?.kind === 'item' && s.selection?.kind === 'items' && s.selection.ids.includes(target.id)) {
      // The gathered group moves without its locked members, a locked one pressed too; they stay and the release says so.
      const { ids, held } = movableItems(level, s.selection.ids, view.surfaceHeight);
      dragRef.current = ids.length ? { ...common, kind: 'group-move', ids, pressed: target.id, base: level, start: view.toPlan(x, y), held }
        : { ...common, kind: 'pan', lastX: x, lastY: y, gathered: true };
    } else if ((target?.kind === 'item' || target?.kind === 'rotate' || target?.kind === 'resize') && level.items.find(item => item.id === target.id)?.locked) {
      // A locked piece is picked as a tap picks it, and pulled it pans: it neither moves, turns nor resizes.
      store.select({ kind: 'item', id: target.id }, anchor);
      dragRef.current = { ...common, kind: 'pan', lastX: x, lastY: y, gathered: true, toggles: true };
    } else if (target?.kind === 'room-move' || (target?.kind === 'room' && s.selection?.kind === 'room' && s.selection.index === target.index)) {
      dragRef.current = { ...common, kind: 'room-move', index: target.index, base: level, valid: true };
    } else if (target?.kind === 'opening-resize') {
      // A Point is taken at once, in Hover too, and its opening keeps its state (the Figma rules, card 12).
      dragRef.current = { ...common, kind: 'opening-resize', id: target.id, base: level, side: target.side };
    } else if(target?.kind==='resize'){
      const item=level.items.find(it=>it.id===target.id)!;
      dragRef.current={...common,kind:'resize',id:item.id,base:level,bounds:view.resizeBounds(item),face:{axis:target.axis,side:target.side}};
    } else if (target?.kind === 'vertex') {
      const wall = level.walls.find(w => w.id === target.wallId)!;
      dragRef.current = { ...common, kind: 'vertex', id: wall.id, end: target.end, base: level, origin: wall[target.end] };
      store.select({ kind: 'vertex', wallId: wall.id, end: target.end }, anchor);
    } else if (target?.kind === 'wall-mid') {
      const wall = level.walls.find(w => w.id === target.id)!;
      dragRef.current = { ...common, kind: 'split', id: wall.id, base: level, origin: wall.center, split: null };
      store.select({ kind: 'wall', id: wall.id, side: 'front' }, anchor);
    } else if (target?.kind === 'wall') {
      dragRef.current = { ...common, kind: 'wall', id: target.id, base: level, segment: wallDragSegment(level,target.id,view.toPlan(x,y)) };
      store.select({ kind: 'wall', id: target.id, side: target.side }, anchor);
    } else if (target?.kind === 'rotate') dragRef.current = { ...common, kind: 'rotate', id: target.id };
    else if (target?.kind === 'item') {
      const item = level.items.find(item => item.id === target.id)!, p = view.toPlan(x, y);
      dragRef.current = { ...common, kind: 'move', gesture: { base: level, sourceId: item.id, copyId: null }, grab: { x: p.x - item.position.x, z: p.z - item.position.z } };
      store.select({ kind: 'item', id: item.id }, anchor);
    } else if (target?.kind === 'opening') {
      dragRef.current = { ...common, kind: 'opening', id: target.id }; store.select({ kind: 'opening', id: target.id }, anchor);
    } else dragRef.current = { ...common, kind: 'pan', lastX: x, lastY: y };
    render();
  }, [hit, render, cancel, previewWall]);

  const updateOpeningDraft = useCallback((base: Level, point: Vec2, kind: OpeningKind, disabled = false) => {
    const view = viewRef.current;
    if (!view) return false;
    hoverRef.current = null;
    const draft = draftRef.current;
    if (draft?.kind === 'opening' && draft.end.x === point.x && draft.end.z === point.z && draft.snapDisabled === disabled) return false;
    const result = projectOpening(base, point, kind, (coarse.current ? 28 : 18) * view.viewport.scale, { scale: view.viewport.scale, disabled });
    const opening = result.level?.openings.at(-1), wall = base.walls.find(w => w.id === opening?.wallId);
    draftRef.current = { kind: 'opening', start: point, end: point, valid: !!result.level, opening, wall, snapDisabled: disabled };
    guidesRef.current = result.level ? result.guides ?? [] : [];
    messageRef.current = result.error ?? null;
    return true;
  }, []);

  const updateDrag = useCallback((input: PointerInput) => {
    const view = viewRef.current, canvas = canvasRef.current, drag = dragRef.current, level = store.level;
    if (!view || !canvas || !drag || !level) return;
    lastInput.current = input;
    const rect = canvas.getBoundingClientRect(), x = input.clientX - rect.left, y = input.clientY - rect.top;
    const dx = x - startRef.current.x, dy = y - startRef.current.y;
    if (drag.kind === 'measure') {
      const snapped = snapVertexPoint(view.toPlan(x, y), drag.base, { scale: view.viewport.scale, disabled: input.ctrlKey || input.metaKey, anchor: drag.start, segments: true });
      measureRef.current = { from: drag.start, to: snapped.point }; guidesRef.current = snapped.guides;
      movedRef.current ||= exceedsDragThreshold(dx, dy); render(); return;
    }
    if (drag.kind === 'insert' || drag.kind === 'room') {
      if (!constructionInScope(drag)) { cancel(); return; }
      const raw = view.toPlan(x, y);
      const point = drag.kind === 'room' ? snapVertexPoint(raw, drag.base, { scale: view.viewport.scale, disabled: input.ctrlKey || input.metaKey }).point : raw;
      if (drag.kind === 'room') {
        if (draftRef.current && draftRef.current.end.x === point.x && draftRef.current.end.z === point.z) return;
        // Furniture is reattached only by the full commit; preview validity depends on walls and openings.
        const result = rectangularRoom({ ...drag.base, items: [] }, drag.start, point);
        draftRef.current = { kind: 'room', start: drag.start, end: point, valid: !!result.level }; messageRef.current = result.error ?? null;
      } else if (!updateOpeningDraft(drag.base, point, drag.openingKind, input.ctrlKey || input.metaKey)) return;
      movedRef.current ||= exceedsDragThreshold(dx, dy); render(); return;
    }
    if (!movedRef.current && !exceedsDragThreshold(dx, dy)) return;
    if (!movedRef.current) {
      movedRef.current = true;
      if (drag.kind === 'pan') store.set({ isDragging: true, selectionAnchor: null });
      else if (!store.beginGeometryStroke(drag.floor)) { cancel(); return; }
    }
    const scale = view.viewport.scale, disabled = input.ctrlKey || input.metaKey;
    const delta = { x: dx * scale, z: dy * scale };
    messageRef.current = null; guidesRef.current = []; invalidRef.current = null; snapRef.current = null;
    if (drag.kind === 'pan') {
      view.panBy(x - drag.lastX, y - drag.lastY); drag.lastX = x; drag.lastY = y;
    } else if(drag.kind==='resize'){
      const item=drag.base.items.find(it=>it.id===drag.id)!,axis=resizeAxisVector(item,drag.face.axis);
      const next=previewItemResize(drag.base,item.id,drag.bounds,drag.face,delta.x*axis.x+delta.z*axis.z);
      store.commitLevel('',()=>next);
    } else if (drag.kind === 'opening-resize') {
      const opening = drag.base.openings.find(value => value.id === drag.id), wall = drag.base.walls.find(value => value.id === opening?.wallId);
      if (!wall) { cancel(); return; }
      // The width sticks to whole 10 and 50 cm; Alt (Option), and Ctrl or ⌘ as everywhere on the plan, set it free.
      const result = previewOpeningResize(drag.base, drag.id, drag.side, delta.x * wall.dir.x + delta.z * wall.dir.z, disabled || input.altKey ? undefined : { scale });
      if (result.level) store.commitLevel('', () => result.level!);
      messageRef.current = result.error ?? null;
    } else if (drag.kind === 'draw') {
      // The moving end trails the pointer through the dead zone, and under a finger it rises above the hand.
      const lift = coarse.current ? TOUCH_ANCHOR_LIFT * Math.min(1, Math.hypot(dx, dy) / (TOUCH_ANCHOR_LIFT * 1.5)) : 0;
      drag.anchor = pullAnchor(drag.anchor, view.toPlan(x, y - lift), (coarse.current ? ANCHOR_ZONE.coarse : ANCHOR_ZONE.fine) * scale);
      const snapped = snapVertexPoint(drag.anchor, drag.base, { scale, disabled, anchor: drag.start, previous: snapCaptureRef.current });
      snapCaptureRef.current = snapped.capture;
      previewWall(drag.base, drag.start, snapped);
    } else if (drag.kind === 'vertex') {
      const raw = { x: drag.origin.x + delta.x, z: drag.origin.z + delta.z };
      const meeting = drag.base.walls.filter(w => samePoint(w.start, drag.origin) || samePoint(w.end, drag.origin));
      const square = meeting.length === 1 ? samePoint(meeting[0].start, drag.origin) ? meeting[0].end : meeting[0].start : undefined;
      const snapped = snapVertexPoint(raw, drag.base, { scale, disabled, origin: drag.origin, exclude: [drag.origin], excludeWalls: meeting.map(w => w.id), previous: snapCaptureRef.current, straight: straightLine(meeting, drag.origin), square });
      snapCaptureRef.current = snapped.capture;
      messageRef.current = store.previewMoveVertex(drag.id, drag.end, snapped.point, snapped.target);
      guidesRef.current = messageRef.current ? [] : snapped.guides;
      // Refused geometry is still shown, in red, where the pointer asked for it; a held capture gets the turquoise mark.
      const wall = drag.base.walls.find(w => w.id === drag.id);
      invalidRef.current = messageRef.current && wall ? { quads: [wallQuad(wall[drag.end === 'start' ? 'end' : 'start'], snapped.point, wall.thickness)], handle: snapped.point } : null;
      snapRef.current = !messageRef.current && (snapped.target || snapped.capture.straight) ? { wallId: drag.id, handle: snapped.point } : null;
    } else if (drag.kind === 'split') {
      // The fake point becomes a corner on the first pull: the wall is cut at its middle inside this
      // stroke, and from then on the corner moves like any other — one undo step for cut and move.
      if (!drag.split) {
        const cut = splitWall(drag.base, drag.id, drag.base.walls.find(w => w.id === drag.id)!.length / 2);
        if (!cut.level) { messageRef.current = cut.error; render(); return; }
        drag.split = cut.level; store.commitLevel('', () => cut.level!); store.select({ kind: 'vertex', wallId: drag.id, end: 'end' });
      }
      const split = drag.split, raw = { x: drag.origin.x + delta.x, z: drag.origin.z + delta.z };
      const pieces = split.walls.filter(w => samePoint(w.start, drag.origin) || samePoint(w.end, drag.origin));
      const snapped = snapVertexPoint(raw, split, { scale, disabled, origin: drag.origin, exclude: [drag.origin], excludeWalls: pieces.map(w => w.id), previous: snapCaptureRef.current, straight: straightLine(pieces, drag.origin) });
      snapCaptureRef.current = snapped.capture;
      const result = moveVertex(split, drag.id, 'end', snapped.point, snapped.target);
      if (result.level) store.commitLevel('', () => result.level!);
      messageRef.current = result.error ?? null; guidesRef.current = result.level ? snapped.guides : [];
      invalidRef.current = result.error ? { quads: pieces.map(w => wallQuad(samePoint(w.start, drag.origin) ? w.end : w.start, snapped.point, w.thickness)), handle: snapped.point } : null;
      snapRef.current = !result.error && (snapped.target || snapped.capture.straight) ? { wallId: drag.id, handle: snapped.point } : null;
    } else if (drag.kind === 'room-move') {
      const constrained = constrainMove(delta, { x: 0, z: 0 }, input.shiftKey, drag.axis); drag.axis = constrained.axis;
      const snapped = snapRoomTranslation(drag.base, drag.index, constrained.point, { scale, disabled, axis: drag.axis });
      const result = moveRoom(drag.base, drag.index, snapped.delta);
      drag.valid = !!result.level;
      if (result.level) store.commitLevel('', () => result.level!);
      messageRef.current = result.error ?? null; guidesRef.current = result.level ? snapped.guides : [];
    } else if (drag.kind === 'wall') {
      const constrained = constrainMove(delta, { x: 0, z: 0 }, input.shiftKey, drag.axis); drag.axis = constrained.axis;
      const snapped = snapWallTranslation(drag.base, drag.id, constrained.point, { scale, disabled, axis: drag.axis, previous: drag.captures, segment: drag.segment });
      messageRef.current = store.previewMoveWall(drag.id, snapped.delta, snapped.connections, drag.segment);
      drag.captures = messageRef.current ? [] : snapped.captures;
      guidesRef.current = messageRef.current ? [] : snapped.guides;
      const wall = drag.segment ?? drag.base.walls.find(w => w.id === drag.id);
      invalidRef.current = messageRef.current && wall ? { quads: [wallQuad({ x: wall.start.x + snapped.delta.x, z: wall.start.z + snapped.delta.z },
        { x: wall.end.x + snapped.delta.x, z: wall.end.z + snapped.delta.z }, wall.thickness)] } : null;
      snapRef.current = !messageRef.current && snapped.connections.length ? { wallId: drag.id } : null;
    } else if (drag.kind === 'move') {
      const p = view.toPlan(x, y), raw = { x: p.x - drag.grab.x, z: p.z - drag.grab.z };
      const source = drag.gesture.base.items.find(item => item.id === drag.gesture.sourceId)!;
      const constrained = constrainMove(raw, source.position, input.shiftKey, drag.axis); drag.axis = constrained.axis;
      // The pointer and the furniture meshes let the item land on what it is dragged across.
      const landing: MoveInput = { pointer: { kind: 'plan', point: p }, probe: view.surfaceHeight, footprints: drag.footprints, grab: drag.grab };
      const copyIdentity = () => store.newItemIdentity({ copyOf: drag.gesture.sourceId });
      const preview = previewItemMove(drag.gesture, constrained.point, input.altKey, copyIdentity, landing);
      const sourceBounds = drag.footprints.get(source.id);
      if (sourceBounds && preview.selectedId !== source.id) drag.footprints.set(preview.selectedId, sourceBounds);
      const snapped = smartSnapItem(preview.level, preview.selectedId, constrained.point, { scale, disabled, axis: drag.axis, origin: source.position,
        surface: store.get().planSurface === 'ceiling' ? 'ceiling' : 'floor', footprints: drag.footprints, previous: capturesRef.current, probe: landing.probe });
      // Re-run the move at the snapped point rather than nudging the item alone: anything
      // standing on it has to arrive with it.
      const settled = snapped.position.x === constrained.point.x && snapped.position.z === constrained.point.z
        ? preview : previewItemMove(drag.gesture, snapped.position, input.altKey, copyIdentity, landing);
      guidesRef.current = snapped.guides; capturesRef.current = snapped.captures;
      store.commitLevel('', () => settled.level);
      store.select({ kind: 'item', id: settled.selectedId });
    } else if (drag.kind === 'group-move') {
      const p = view.toPlan(x, y), raw = { x: p.x - drag.start.x, z: p.z - drag.start.z };
      const constrained = constrainMove(raw, { x: 0, z: 0 }, input.shiftKey, drag.axis); drag.axis = constrained.axis;
      const moved = moveItemGroup(drag.base, drag.ids, constrained.point, view.surfaceHeight);
      store.commitLevel('', () => moved);
    } else if (drag.kind === 'rotate') {
      const item = level.items.find(item => item.id === drag.id); if (!item) return;
      let yaw = yawTowards(item.position, view.toPlan(x, y));
      const angle = Math.round(yaw / (Math.PI / 4)) * (Math.PI / 4);
      if (!disabled && Math.abs(yaw - angle) < 4 * Math.PI / 180) yaw = angle;
      store.rotateItem(drag.id, yaw);
    } else if (drag.kind === 'opening') {
      const opening = level.openings.find(opening => opening.id === drag.id), wall = level.walls.find(wall => wall.id === opening?.wallId);
      if (opening && wall) {
        // Along its wall an opening seeks the middle, the centre between its neighbours, or the next step of an even row.
        const snapped = snapOpeningOffset(level, wall.id, opening.width, projectPoint(view.toPlan(x, y), wall.start, wall.end).t * wall.length, { scale, disabled, exclude: opening.id });
        messageRef.current = store.moveOpening(opening.id, snapped.offset);
        guidesRef.current = messageRef.current ? [] : snapped.guides;
      }
    }
    render();
  }, [render, cancel, updateOpeningDraft, previewWall]);

  /**
   * The wall tool's own hover: the Wall Anchor under the pointer with its alignment, and — with a
   * chain waiting — the next wall from the chain's end. Nothing here touches the document.
   */
  const updateWallHover = useCallback((level: Level, x: number, y: number, input: PointerInput): boolean => {
    const view = viewRef.current;
    if (!view) return false;
    hoverRef.current = null;
    const scale = view.viewport.scale, chain = chainRef.current;
    const snapped = snapVertexPoint(view.toPlan(x, y), level, { scale, disabled: input.ctrlKey || input.metaKey, anchor: chain ?? undefined, previous: snapCaptureRef.current });
    snapCaptureRef.current = snapped.capture;
    const before = JSON.stringify([draftRef.current, anchorRef.current, guidesRef.current, messageRef.current]);
    if (chain) { anchorRef.current = null; previewWall(level, chain, snapped); }
    else {
      draftRef.current = null; messageRef.current = null;
      anchorRef.current = { point: snapped.point, snapped: !!snapped.target }; guidesRef.current = snapped.guides;
    }
    return before !== JSON.stringify([draftRef.current, anchorRef.current, guidesRef.current, messageRef.current]);
  }, [previewWall]);

  applyInput.current = (input: PointerInput) => {
    if (dragRef.current) { updateDrag(input); return true; }
    const view = viewRef.current, level = store.level, canvas = canvasRef.current, s = store.get();
    if (!view || !level || !canvas || s.mode !== 'plan') return false;
    const rect = canvas.getBoundingClientRect(), x = input.clientX - rect.left, y = input.clientY - rect.top;
    if (panOnly()) {
      // A pan-only press: nothing under the pointer lights up and no tool previews.
      const changed = hoverRef.current !== null || anchorRef.current !== null || draftRef.current?.kind === 'opening';
      hoverRef.current = null; anchorRef.current = null;
      if (draftRef.current?.kind === 'opening') draftRef.current = null;
      return changed;
    }
    if (s.tool === 'door' || s.tool === 'window' || s.tool === 'arch') return updateOpeningDraft(level, view.toPlan(x, y), s.tool, input.ctrlKey || input.metaKey);
    if (s.tool === 'wall') return updateWallHover(level, x, y, input);
    const presentedTarget = s.selection?.kind === 'opening' && s.propertiesPreview ? hit(view, x, y, store.presentedLevel ?? level) : null;
    const target = presentedTarget?.kind === 'opening-resize' ? presentedTarget : hit(view, x, y, level);
    const label = s.tool === 'navigate' && !s.resizeMode && s.edit !== 'items' && !labelBlocked(target) ? view.labelAt(x, y) : null;
    const labelKey = label ? dimensionLabelKey(label) : null, labelChanged = labelKey !== labelHover.current;
    labelHover.current = labelKey;
    if (JSON.stringify(target) === JSON.stringify(hoverRef.current)) return labelChanged;
    hoverRef.current = target; return true;
  };
  const onPointerMove = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    coarse.current = e.pointerType === 'touch' || (typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches);
    if (e.pointerType === 'touch' && touches.current.has(e.pointerId)) touches.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (navigating.current) { render(); return; }
    if (dragRef.current && pointerRef.current !== e.pointerId) return;
    lastInput.current = pointerInput(e); pendingInput.current = lastInput.current; render(false);
  }, [render]);

  const onPointerUp = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    const view = viewRef.current, drag = dragRef.current;
    if (navigating.current) {
      if (!touches.current.has(e.pointerId)) return;
      updatePinch(); touches.current.delete(e.pointerId); pinch.current = null;
      if (!touches.current.size) { navigating.current = false; store.set({ isDragging: false }); }
      if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
      render(); return;
    }
    if (!drag || pointerRef.current !== e.pointerId) return;
    touches.current.delete(e.pointerId); pendingInput.current = null;
    updateDrag(pointerInput(e));
    if (dragRef.current !== drag) return;
    if (drag.kind === 'measure') {
      dragRef.current = null; guidesRef.current = [];
      const line = measureRef.current; measureRef.current = null;
      if (line && Math.hypot(line.to.x - line.from.x, line.to.z - line.from.z) >= .01) store.addMeasurement(line.from, line.to);
      render(); return;
    }
    if (drag.kind === 'insert' || drag.kind === 'room') {
      const rect = e.currentTarget.getBoundingClientRect();
      if (e.clientX < rect.left || e.clientX > rect.right || e.clientY < rect.top || e.clientY > rect.bottom) { cancel(); return; }
      finishConstruction(e.clientX, e.clientY); return;
    }
    dragRef.current = null;
    if (drag.kind === 'draw') {
      const draft = draftRef.current;
      if (draft?.valid && (movedRef.current || drag.chain)) {
        committingRef.current = true;
        try { messageRef.current = store.addWall(draft.start, draft.end); } finally { committingRef.current = false; }
        // The next wall starts where this one ended, until Escape or leaving the tool.
        if (!messageRef.current) chainRef.current = draft.end;
      } else if (!movedRef.current && !drag.chain) {
        // One press with no pull starts a wall: from here it follows the pointer until the next press.
        chainRef.current = drag.start;
      }
    }
    // Touch has no hover pass after release: retain the refused wall and its reason until the next action.
    const rejectedWallDraft = drag.kind === 'draw' && messageRef.current ? draftRef.current : null;
    draftRef.current = rejectedWallDraft; guidesRef.current = []; capturesRef.current = []; invalidRef.current = null; snapRef.current = null;
    const rejectedRoomMessage = drag.kind === 'room-move' && !drag.valid ? messageRef.current : null;
    if (drag.kind === 'room-move' && !drag.valid) store.cancelStroke();
    else if (drag.kind === 'draw') {
      // The stroke's end gives the drawn wall its UniqueId, a new scene the chain belongs to.
      committingRef.current = true;
      try { store.endStroke(); } finally { committingRef.current = false; }
    } else store.endStroke();
    if (rejectedRoomMessage) messageRef.current = rejectedRoomMessage;
    pointerRef.current = null;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
    const level = store.level;
    if (!view || !level) return;
    const anchor = { x: e.clientX, y: e.clientY };
    const rect = e.currentTarget.getBoundingClientRect();
    if (store.get().tool === 'wall') { if (e.pointerType !== 'touch') updateWallHover(level, e.clientX - rect.left, e.clientY - rect.top, pointerInput(e)); }
    else hoverRef.current = panOnly() ? null : hit(view, e.clientX - rect.left, e.clientY - rect.top, level);
    if (!movedRef.current && drag.kind === 'pan' && drag.secondary && store.get().tool === 'wall') {
      // The right button, pressed and released in place, finishes drawing the way Escape does:
      // the chain first, then the tool. Dragged, it still pans.
      if (chainRef.current || draftRef.current) { chainRef.current = null; clearHoverPreview(); }
      else store.set({ tool: 'navigate' });
      render(); return;
    }
    const tappedLabel = pressLabel.current;
    pressLabel.current = null;
    if (!movedRef.current && tappedLabel && (drag.kind === 'pan' || drag.kind === 'room-move' || drag.kind === 'wall')) {
      if (drag.kind !== 'pan') store.select(drag.initialSelection, drag.initialAnchor);
      setTyping({ label: tappedLabel, at: { x: rect.left + tappedLabel.x, y: rect.top + tappedLabel.y } });
      render(); return;
    }
    if (movedRef.current && drag.kind === 'group-move') store.notifyLockedStayed(drag.held);
    if (!movedRef.current && drag.kind === 'group-move') {
      // A tap on one of the gathered items, with no pull, keeps only that one; with Shift it leaves the group.
      store.select(e.shiftKey ? toggleItemSelection(drag.initialSelection, drag.pressed) : { kind: 'item', id: drag.pressed }, anchor);
    } else if (!movedRef.current && drag.kind === 'pan' && drag.toggles) {
      store.select(nextSelectionOnTap(drag.initialSelection, store.get().selection), anchor);
    } else if (!movedRef.current && drag.kind === 'pan' && !drag.secondary && !drag.space && !drag.gathered) {
      const target = hoverRef.current;
      const tapped = target?.kind === 'room' ? { kind: 'room' as const, index: target.index } : null;
      // In resize mode a tap beside the item means «done»: the mode ends and the item stays selected.
      if (store.get().resizeMode) store.setResizeMode(false);
      else store.select(nextSelectionOnTap(drag.initialSelection, tapped), anchor);
      store.set({ lastFloorPoint: view.toPlan(e.clientX - rect.left, e.clientY - rect.top) });
    } else if (!movedRef.current && (drag.kind === 'room-move' || drag.kind === 'move' || drag.kind === 'wall' || drag.kind === 'split' || drag.kind === 'opening' || drag.kind === 'vertex')) {
      // The press already selected this object; a tap with no movement toggles it off. The resized
      // item, still in resize mode (a press on another one would have ended it), keeps its selection.
      if (drag.kind === 'move' && store.get().resizeMode) store.setResizeMode(false);
      else store.select(nextSelectionOnTap(drag.initialSelection, store.get().selection), anchor);
    } else if (!movedRef.current && drag.kind === 'opening-resize') {
      // A press on a Point that did not pull it is a click on its opening.
      store.select({ kind: 'opening', id: drag.id }, anchor);
    } else if (drag.kind !== 'pan') store.set({ selectionAnchor: anchor });
    render();
  }, [hit, render, updateDrag, updatePinch, cancel, updateWallHover, clearHoverPreview]);

  function constructionInScope(drag: Drag & { base: Level }): boolean {
    const s = store.get();
    return drag.projectId === s.projectId && drag.levelIndex === s.activeLevelIndex && drag.base === store.level && s.mode === 'plan'
      && s.tool === (drag.kind === 'insert' ? drag.openingKind : 'room');
  }

  function finishConstruction(clientX: number, clientY: number) {
    const drag = dragRef.current, view = viewRef.current, canvas = canvasRef.current;
    if (!drag || (drag.kind !== 'insert' && drag.kind !== 'room') || !view || !canvas) return;
    if (!constructionInScope(drag)) { cancel(); return; }
    const rect = canvas.getBoundingClientRect(), point = view.toPlan(clientX - rect.left, clientY - rect.top);
    const before = store.level!;
    dragRef.current = null; draftRef.current = null; pendingInput.current = null;
    if (drag.kind === 'room') {
      const end = snapVertexPoint(point, before, { scale: view.viewport.scale, disabled: lastInput.current?.ctrlKey || lastInput.current?.metaKey }).point;
      messageRef.current = store.addRectangularRoom(drag.start, end);
      if (!messageRef.current) {
        const centre = { x: (drag.start.x + end.x) / 2, z: (drag.start.z + end.z) / 2 };
        const room = store.level?.rooms.find(r => contains(r.polygon, centre));
        if (room) store.select({ kind: 'room', index: room.index }, { x: clientX, y: clientY });
      }
    } else {
      const result = projectOpening(before, point, drag.openingKind, (coarse.current ? 28 : 18) * view.viewport.scale, { scale: view.viewport.scale, disabled: lastInput.current?.ctrlKey || lastInput.current?.metaKey });
      const opening = result.level?.openings.at(-1);
      messageRef.current = opening ? store.addOpening(opening.wallId, drag.openingKind, opening.offset) : result.error ?? null;
      if (!messageRef.current) { const added = store.level?.openings.at(-1); if (added) store.select({ kind: 'opening', id: added.id }, { x: clientX, y: clientY }); }
    }
    const pointer = pointerRef.current; pointerRef.current = null;
    if (pointer !== null && canvas.hasPointerCapture(pointer)) canvas.releasePointerCapture(pointer);
    if (!messageRef.current) store.set({ tool: 'navigate' });
    render();
  }

  // A trackpad pinch reaches the page as a ctrl-wheel, and in Safari as its own gesture events.
  // Left to the browser both zoom the whole interface instead of the plan, and React registers
  // `wheel` passively at the root, so the handler that refuses that default has to be a native one.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const zoomAt = (clientX: number, clientY: number, factor: number) => {
      const view = viewRef.current;
      if (!view || dragRef.current || !Number.isFinite(factor)) return;
      navigationIntent.current?.();
      const rect = canvas.getBoundingClientRect();
      stopReveal(); clearHoverPreview();
      view.zoomAt(clientX - rect.left, clientY - rect.top, factor); render();
    };
    const wheel = (e: WheelEvent) => {
      e.preventDefault();              // nothing behind the plan scrolls, and the page never zooms
      if (gesturing.current && e.ctrlKey) return;   // Safari already sends this pinch as a gesture
      const step = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? canvas.clientHeight : 1;
      // A pinch comes in far smaller steps than a wheel notch, so it zooms at its own rate.
      zoomAt(e.clientX, e.clientY, zoomStep(e.deltaY * step * (e.ctrlKey ? PINCH_RATE : WHEEL_RATE)));
    };
    const gesture = (e: GestureLikeEvent) => {
      e.preventDefault();
      if (e.type === 'gestureend') { gesturing.current = false; return; }
      const spread = Math.max(0.05, e.scale || 1);
      if (e.type === 'gesturestart') { gesturing.current = true; gestureSpread.current = spread; return; }
      zoomAt(e.clientX, e.clientY, zoomStep(Math.log(gestureSpread.current / spread)));
      gestureSpread.current = spread;
    };
    // ⌘± zooms at the pointer while it is over the plan; zoomAt clears the hover input, so the spot is kept apart.
    let pointerAt: { x: number; y: number } | null = null;
    const track = (e: PointerEvent) => { pointerAt = { x: e.clientX, y: e.clientY }; };
    const untrack = () => { pointerAt = null; };
    const keyZoom = (event: Event) => {
      const detail = (event as CustomEvent<number | 'reset'>).detail, rect = canvas.getBoundingClientRect(), scale = viewRef.current?.viewport.scale ?? PLAN_SCALE_100;
      if (detail === 'reset') zoomAt(rect.left + rect.width / 2, rect.top + rect.height / 2, PLAN_SCALE_100 / scale);
      else zoomAt(pointerAt?.x ?? rect.left + rect.width / 2, pointerAt?.y ?? rect.top + rect.height / 2, detail);
    };
    canvas.addEventListener('wheel', wheel, { passive: false });
    for (const type of GESTURE_EVENTS) canvas.addEventListener(type, gesture as EventListener, { passive: false });
    canvas.addEventListener('pointermove', track); canvas.addEventListener('pointerleave', untrack);
    window.addEventListener('hp:zoom-plan', keyZoom);
    return () => {
      window.removeEventListener('hp:zoom-plan', keyZoom);
      canvas.removeEventListener('pointermove', track); canvas.removeEventListener('pointerleave', untrack);
      canvas.removeEventListener('wheel', wheel);
      for (const type of GESTURE_EVENTS) canvas.removeEventListener(type, gesture as EventListener);
      gesturing.current = false;
    };
  }, [render, clearHoverPreview, stopReveal]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (target?.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target?.tagName)) return;
      const state = store.get();
      if (state.showProperties || state.materialTarget || state.showCatalog || state.showVariations || state.showLighting || state.showGallery || state.cameraMode || state.showMinimap || state.showWindowViews) return;
      // A question asked over the plan owns the keyboard until it is answered: its Escape is only a cancel.
      if (state.wallRemoval || state.roomAction || document.querySelector('.modal-backdrop > [aria-modal="true"]')) return;
      if (['Alt', 'Shift', 'Control', 'Meta'].includes(e.key) && lastInput.current) {
        lastInput.current = { ...lastInput.current, altKey: e.altKey, shiftKey: e.shiftKey, ctrlKey: e.ctrlKey, metaKey: e.metaKey };
        if (dragRef.current) { e.preventDefault(); updateDrag(lastInput.current); } else { pendingInput.current = lastInput.current; render(); }
        return;
      }
      if (e.type === 'keyup') return;
      if (e.key === 'Escape') {
        // Escape ends the chain first; the tool itself goes on the next press.
        if (dragRef.current || navigating.current || draftRef.current?.kind === 'opening') { const drawing = dragRef.current?.kind === 'draw'; cancel(); if (drawing) chainRef.current = null; }
        else if (chainRef.current) { chainRef.current = null; clearHoverPreview(); render(); }
        else if (state.resizeMode) store.setResizeMode(false);
        else { store.select(null); store.set({ tool: 'navigate' }); }
      }
      // Undo/redo/delete/duplicate/fit live in EditorView, keyed by the physical key so they
      // work in every keyboard layout and in both modes; this handler keeps only what needs
      // the plan itself.
    };
    const onBlur = () => { if (dragRef.current || navigating.current || ['door', 'window', 'arch', 'room'].includes(store.get().tool)) cancel(); else { clearHoverPreview(); render(); } };
    // Space down or up changes the cursor and the hover until a press comes.
    const unsubscribeSpace = onSpace(() => { if (lastInput.current && !dragRef.current) pendingInput.current = lastInput.current; render(); });
    window.addEventListener('keydown', onKey); window.addEventListener('keyup', onKey); window.addEventListener('blur', onBlur);
    return () => { unsubscribeSpace(); window.removeEventListener('keydown', onKey); window.removeEventListener('keyup', onKey); window.removeEventListener('blur', onBlur); };
  }, [render, cancel, updateDrag, clearHoverPreview]);

  const submitTyped = (value: number): string | null => {
    const label = typingRef.current?.label;
    if (!label) return null;
    if (store.get().planScale) return store.scalePlanTo(label.kind === 'wall' ? 'length' : 'area', value, label.value);
    if (label.kind === 'wall') return store.setWallLength(label.id, value);
    return store.applyProperties({ kind: 'room', id: String(label.index), values: { area: value } });
  };
  const closeTyping = () => { setTyping(null); render(); canvasRef.current?.focus({ preventScroll: true }); };
  return <>{typing && <DimensionInput key={dimensionLabelKey(typing.label)} label={typing.label} at={typing.at} scaleMode={!!state.planScale}
    onSubmit={submitTyped} onClose={closeTyping} />}<canvas ref={canvasRef} className="plan-canvas" tabIndex={0} aria-label={tr('#EditorPlanCanvasLabel')}
    title={tr('#EditorPlanCanvasTooltip')}
    onDragOver={event => {
      if (!event.dataTransfer.types.includes('text/hp-opening') || dragRef.current?.kind !== 'insert') return;
      event.preventDefault(); event.dataTransfer.dropEffect = 'copy';
      pendingInput.current = pointerInput(event); render(false);
    }}
    onDragLeave={() => { if (dragRef.current?.kind === 'insert' && pointerRef.current === null) { pendingInput.current = null; draftRef.current = null; messageRef.current = null; render(); } }}
    onDrop={event => {
      if (!event.dataTransfer.types.includes('text/hp-opening')) return;
      event.preventDefault(); event.stopPropagation(); finishConstruction(event.clientX, event.clientY);
    }}
    style={{ touchAction: 'none' }} onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp}
    onContextMenu={e => e.preventDefault()}
    onPointerLeave={event => {
      // A lifted finger emits pointerleave even though the user has not dismissed the refusal.
      if (event.pointerType === 'touch' && store.get().tool === 'wall' && messageRef.current) return;
      if (!dragRef.current) { clearHoverPreview(); render(); }
    }}
    onPointerCancel={event => { if (pointerRef.current === event.pointerId || touches.current.has(event.pointerId)) cancel(); }} onLostPointerCapture={event => { if (pointerRef.current === event.pointerId || touches.current.has(event.pointerId)) cancel(); }} /></>;
}
